/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict'

import { useState, useEffect, useContext } from 'react';
import { useTheme } from 'styled-components';
import styled from 'styled-components';
import { Button, Alert } from 'react-bootstrap';
import { ColumnDef, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { Container, Table as BTable } from 'react-bootstrap';
import { FileEntry, listFiles, deleteFile } from '../data';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faPen, faTrash, faPersonRunning, faQ } from '@fortawesome/free-solid-svg-icons';
import { MetadataContext } from './MetadataContext';
import { StatusContext } from './StatusContext';

interface FileListProps {
    cloud: boolean;
    prefix?: string;
    allowDelete?: boolean;
    allowLoad?: boolean;
    clickFile?: (filename: string) => void;
    filter?: RegExp;
    showSearch?: boolean;
    search?: string;
    onSearchChange?: (value: string) => void;
}

function FileList({
    cloud,
    prefix,
    allowDelete,
    allowLoad,
    clickFile,
    filter,
    showSearch = true,
    search,
    onSearchChange
} : FileListProps) {
    const theme: any = useTheme();
    const [flist, setFlist] = useState<FileEntry[]>([]);
    const [rowSelection, setRowSelection] = useState({});
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [refresh, setRefresh] = useState<number>(0);
    const [loadError, setLoadError] = useState<string>('');
    const statusContext = useContext<any>(StatusContext);
    const metadataContext = useContext<any>(MetadataContext);
    const columns: ColumnDef<FileEntry>[] = [
        {
            header: 'Filename',
            accessorKey: 'name',
        },
        {
            header: 'Size',
            accessorKey: 'size',
        },
        {
            header: 'Modified',
            accessorKey: 'modified',
            cell: (props) => {
                const ts = Number(props.getValue());
                if (!ts || Number.isNaN(ts)) {
                    return '';
                }
                return new Date(ts).toLocaleString();
            }
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (props) => {
                const row = props.row.original;
                return (
                    <div className="d-flex">
                        {allowLoad &&
                            <Button
                                size="sm"
                                className="me-2"
                                variant="outline-success"
                                onClick={(e) => {
                                }}
                                style={{
                                    backgroundColor: 'transparent',
                                    borderColor: theme.colors.light,
                                    color: theme.colors.light,
                                }}
                            >
                                <FontAwesomeIcon icon={faPersonRunning} />
                            </Button>
                        }
                        {allowDelete &&
                            <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => {
                                        const key = prefix ? `${prefix}/${row.name}` : row.name;
                                        deleteFile(cloud, key).then(() => {
                                        setRefresh(r => r + 1);
                                        statusContext.postInfo(`Deleted file ${row.name}`);
                                    }).catch((err : any) => {
                                        statusContext.postError(`Failed to delete file ${row.name}: ${err.message}`);
                                    });      
                                }}
                                style={{
                                    backgroundColor: 'transparent',
                                    borderColor: theme.colors.danger,
                                    color: theme.colors.danger
                                }}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        }
                    </div>
                );
            }
        },
    ];

    useEffect(() => {
        async function fetchFiles() {
            try {
                setLoadError('');
                const activePrefix = prefix || metadataContext.prefix || '';
                const listPrefix = activePrefix ? `${activePrefix}/` : '';
                const files = await listFiles(cloud, listPrefix);
                if (filter) {
                    setFlist(files.filter(f => filter.test(f.name)));
                } else {
                    setFlist(files);
                }
            } catch (err: any) {
                setFlist([]);
                setLoadError(err?.message || 'Failed to list files');
            }
        }
        fetchFiles();
    }, [cloud, prefix, refresh]);

    const effectiveFilter = search !== undefined ? search : globalFilter;

    const table = useReactTable({
        data: flist,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        globalFilterFn: 'includesString',
        enableGlobalFilter: true,
        getRowId: (row: any) => row.name.toString(),
        state: {
            globalFilter: effectiveFilter
        },
        initialState: {
            sorting: [{ id: 'name', desc: true }],
        }
    });

    useEffect(() => {
        table.setGlobalFilter(effectiveFilter || '');
    }, [effectiveFilter]);

    return (
        <Container style={{ backgroundColor: theme.colors.tableBg, color: theme.colors.ctext }}>
            {loadError && (
                <Alert variant="danger" className="mb-3">
                    {loadError}
                </Alert>
            )}
            {showSearch && (
                <div className="mb-3">
                    <input
                        value={effectiveFilter ?? ''}
                        onChange={e => {
                            const newValue = String(e.target.value);
                            if (search !== undefined && onSearchChange) {
                                onSearchChange(newValue);
                            } else {
                                setGlobalFilter(newValue);
                            }
                            table.setGlobalFilter(newValue);
                        }}
                        className="form-control"
                        placeholder="Search files..."
                        style={{
                            backgroundColor: theme.colors.bg,
                            color: theme.colors.ctext,
                            borderColor: theme.colors.border
                        } as any}
                    />
                </div>
            )}
            <div className="flist-table-wrap" style={{ maxHeight: '350px', overflowY: 'auto', position: 'relative' }}>
                <BTable 
                    style={{ 
                        "--bs-table-bg": theme.colors.tableBg,
                        "--bs-table-color": theme.colors.ctext,
                    } as any}
                    className="flist-table"
                >
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: theme.colors.tableBg }}>
                        {table.getHeaderGroups().map(headerGroup => (
                        <tr 
                            key={headerGroup.id}>{headerGroup.headers.map(header => (
                            <th 
                                key={header.id} 
                                colSpan={header.colSpan}
                            >
                                <div 
                                    onClick={header.column.getToggleSortingHandler()}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {header.isPlaceholder ? null : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                    {header.column.getIsSorted() ? (
                                        <SortIcon>
                                            <FontAwesomeIcon icon={header.column.getIsSorted() === 'desc' ? faCaretDown : faCaretUp} />
                                        </SortIcon>
                                    ) : null}
                                </div>
                            </th>
                        ))}</tr>
                    ))}</thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                        <tr 
                            key={row.id} 
                            onClick={() => clickFile && clickFile(row.original.name)}
                            style={{
                                backgroundColor: row.getIsSelected() 
                                    ? (theme.colors.selectedRowBg || '#384152') 
                                    : theme.colors.tableBg,
                            }}
                        >
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id} >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}</tbody>
                </BTable>
            </div>
        </Container>
    );
}

const SortIcon = styled.span`
    margin-left: 5px;
`;

export default FileList;